import { observable, action, computed } from 'mobx';
import moment from 'moment';
import * as api from '../services/api';
import UserStore from './common/UserStore';
import ComponentStockStore from './ComponentStockStore';

const DATE_FORMAT = 'DD-MM-YYYY';
const METABASE_DATE_FORMAT = 'YYYY-MM-DD';

class HubPerformanceStore {
    @observable loading = true;
    @observable targetsLoading = false;
    @observable stockLoading = false;
    @observable targetRows = [];
    @observable todayOrders = [];
    @observable yesterdayOrders = [];
    @observable monthToDateOrders = [];
    @observable criticalComponentsOutOfStock = [];
    @observable targetError = null;

    @computed get selectedHubCode() {
        return UserStore.selectedHub && UserStore.selectedHub.value
            ? UserStore.selectedHub.value
            : '';
    }

    isCompletedSale = (order) => {
        return (
            (order.payment_method === 'Cash' && order.status === 'Delivered') ||
            (order.transaction_status === 'Complete' && order.status !== 'Cancelled')
        );
    };

    getAggDeliveryFee = (order) => {
        return order.aggregator_order_id ? order.delivery_charge || 0 : 0;
    };

    getRevenue = (orders = []) => {
        return orders.filter(this.isCompletedSale).reduce((acc, order) => {
            return acc + (order.net_total || 0) - this.getAggDeliveryFee(order);
        }, 0);
    };

    getTransactions = (orders = []) => {
        return orders.filter(this.isCompletedSale).length;
    };

    getAverageBasket = (orders = []) => {
        const transactions = this.getTransactions(orders);
        if (!transactions) {
            return 0;
        }
        return this.getRevenue(orders) / transactions;
    };

    getCancelledOrders = (orders = []) => {
        return orders.filter((order) => order.status === 'Cancelled').length;
    };

    isProductionCompleted = (item) => {
        if (item.item_type === 'Combo') {
            return item.combo_sub_items && item.combo_sub_items.every(this.isProductionCompleted);
        }
        return item.production_completed || false;
    };

    getAverageServiceSpeedMinutes = (orders = []) => {
        const serviceTimes = orders.reduce((acc, order) => {
            if (
                order.menu_item &&
                order.menu_item.every(this.isProductionCompleted) &&
                order.production_started_at &&
                order.last_scanned_at
            ) {
                acc.push((order.last_scanned_at - order.production_started_at) / 60000);
            }
            return acc;
        }, []);

        if (!serviceTimes.length) {
            return 0;
        }

        return serviceTimes.reduce((acc, serviceTime) => acc + serviceTime, 0) / serviceTimes.length;
    };

    getTargetRow = (date) => {
        const targetDate = moment(date, DATE_FORMAT).format(METABASE_DATE_FORMAT);
        return (this.targetRows || []).find((row) => {
            return row['Hub Code'] === this.selectedHubCode && row['Target Date'] === targetDate;
        });
    };

    getMonthToDateTargetRows = () => {
        const today = moment();
        return (this.targetRows || []).filter((row) => {
            const targetDate = moment(row['Target Date'], METABASE_DATE_FORMAT);
            return (
                row['Hub Code'] === this.selectedHubCode &&
                targetDate.isSame(today, 'month') &&
                targetDate.isSameOrBefore(today, 'day')
            );
        });
    };

    sumTargetRows = (targetRows, key) => {
        return (targetRows || []).reduce((acc, row) => acc + Number(row[key] || 0), 0);
    };

    @computed get todayTarget() {
        return this.getTargetRow(moment().format(DATE_FORMAT));
    }

    @computed get yesterdayTarget() {
        return this.getTargetRow(moment().subtract(1, 'day').format(DATE_FORMAT));
    }

    @computed get monthToDateWinningRevenueTarget() {
        return this.sumTargetRows(this.getMonthToDateTargetRows(), 'Winning Daily Revenue Target Rm');
    }

    @action handleGetTargets = () => {
        this.targetsLoading = true;
        this.targetError = null;

        return api.getOutletPerformanceTargets()
            .then((rows) => {
                this.targetRows = rows || [];
            })
            .catch((err) => {
                this.targetRows = [];
                this.targetError = err.message;
            })
            .finally(() => {
                this.targetsLoading = false;
            });
    };

    @action handleGetCriticalComponentsOutOfStock = () => {
        this.stockLoading = true;

        return ComponentStockStore.handleGetCriticalStockStatus()
            .then(() => {
                this.criticalComponentsOutOfStock = (ComponentStockStore.criticalStockStatus || [])
                    .filter((stock) => stock.unsold <= 0 && !stock.is_sub_component_available)
                    .map((stock) => stock.name && stock.name !== '-' ? stock.name : stock.sku);
            })
            .catch((err) => {
                UserStore.message = err.message;
                this.criticalComponentsOutOfStock = [];
            })
            .finally(() => {
                this.stockLoading = false;
            });
    };

    @action handleGetOrdersForDate = (date) => {
        return api.getAllOrders(date).then((res) => {
            const orders = Array.isArray(res) ? res : [];
            return orders.filter((order) => {
                const buildingName = order.address && order.address.building_name;
                const isPopMeals = buildingName === 'Pop Meals Office' || buildingName === 'Pop Meals @ Kokubu Food Logistics';
                return isPopMeals || order.production_started_at;
            });
        });
    };

    @action handleGetMonthToDateOrders = () => {
        const start = moment().startOf('month');
        const today = moment();
        const dates = [];
        const cursor = start.clone();

        while (cursor.isSameOrBefore(today, 'day')) {
            dates.push(cursor.format(DATE_FORMAT));
            cursor.add(1, 'day');
        }

        return Promise.all(dates.map((date) => this.handleGetOrdersForDate(date)))
            .then((ordersByDate) => ordersByDate.reduce((acc, orders) => acc.concat(orders), []));
    };

    @action handleGetPerformanceData = () => {
        this.loading = true;
        const today = moment().format(DATE_FORMAT);
        const yesterday = moment().subtract(1, 'day').format(DATE_FORMAT);

        return Promise.all([
            this.handleGetOrdersForDate(today),
            this.handleGetOrdersForDate(yesterday),
            this.handleGetMonthToDateOrders(),
            this.handleGetTargets(),
            this.handleGetCriticalComponentsOutOfStock(),
        ])
            .then(([todayOrders, yesterdayOrders, monthToDateOrders]) => {
                this.todayOrders = todayOrders;
                this.yesterdayOrders = yesterdayOrders;
                this.monthToDateOrders = monthToDateOrders;
            })
            .catch((err) => {
                UserStore.message = err.message;
            })
            .finally(() => {
                this.loading = false;
            });
    };
}

const store = new HubPerformanceStore();
export default store;
